import { useCallback, useEffect, useRef, useState } from 'react';

import { CLICK_IDS } from '@sunflower-games/analytics-events';

import { useGoHome } from '../../../hooks/useGoHome';
import { useSideMenu } from '../../../hooks/useSideMenu';
import { useToggleCoin } from '../../../hooks/useToggleCoin';
import { intercom } from '../../../services/intercom';
import { useStores } from '../../../stores/useStores';
import { devLog } from '../../../utils/log';

const MAX_RETRIES = 2;

type ConnectionInfo = { effectiveType?: string; downlink?: number; rtt?: number };

const getConnection = (): ConnectionInfo | undefined =>
  (navigator as Navigator & { connection?: ConnectionInfo }).connection;

const getLoadTimeoutMs = () => {
  const effectiveType = getConnection()?.effectiveType;
  if (effectiveType === 'slow-2g' || effectiveType === '2g') return 25_000;
  if (effectiveType === '3g') return 20_000;
  return 10_000;
};

const getTimeoutDiagnostics = () => {
  const conn = getConnection();

  return {
    connection: conn ? { effectiveType: conn.effectiveType, downlink: conn.downlink, rtt: conn.rtt } : 'unavailable',
    online: navigator.onLine,
  };
};

export const useGameIframeEvents = ({ iframeRef }: { iframeRef: React.RefObject<HTMLIFrameElement> }) => {
  const { coinTypeStore, gameStore, payRedeemStore, prizeDropStore, uiStore, minigamesStore, lowBalanceStore } =
    useStores();
  const { handleGoHome } = useGoHome();
  const { toggleCoin } = useToggleCoin();
  const { handleToggle } = useSideMenu();

  const { prizeDropEventByCoin } = prizeDropStore;

  const iframeElRef = useRef<HTMLIFrameElement | null>(null);
  const retryCountRef = useRef(0);
  const loadStartTimeRef = useRef<number>(0);

  const [isIframeLoading, setIsIframeLoading] = useState(!!gameStore.gameUiUrl);

  const getElapsedMs = () =>
    loadStartTimeRef.current ? Math.round(performance.now() - loadStartTimeRef.current) : null;

  const onGameIframeError = () => {
    console.error(`Error loading Game UI iframe | ${JSON.stringify({ elapsedMs: getElapsedMs() })}`);
    gameStore.setIsGameUiError(true);
    setIsIframeLoading(false);
  };

  const retryIframeLoad = useCallback(() => {
    console.log(`Game UI iframe manual retry by user | ${JSON.stringify(getTimeoutDiagnostics())}`);
    retryCountRef.current = 0;
    loadStartTimeRef.current = performance.now();
    gameStore.setSlotsGameUiIframe(null);
    gameStore.setIsGameUiError(false);
    setIsIframeLoading(!!gameStore.gameUiUrl);
  }, [gameStore]);

  const onIframeLoad = useCallback(() => {
    console.log(`Game UI iframe loaded | ${JSON.stringify({ elapsedMs: getElapsedMs() })}`);
    setIsIframeLoading(false);
  }, []);

  const onGameUiMessage = useCallback(
    (event: MessageEvent) => {
      const { data, origin } = event;

      if (origin !== import.meta.env.VITE_GAME_UI_URL || data.source !== 'game') {
        return;
      }

      if (gameStore.isGameUiError) {
        devLog(
          `useGameIframeEvents Ignoring message from game UI because it is in error state: ${JSON.stringify(data)}`,
        );
        return;
      }

      devLog(`useGameIframeEvents Received message from game UI: ${JSON.stringify(data)}`);

      switch (data.type) {
        case 'gameUiReadyForMessages': {
          const payloadType = data.payload?.type;
          console.log(
            `Game UI iframe gameUiReadyForMessages | ${JSON.stringify({ elapsedMs: getElapsedMs(), payloadType })}`,
          );

          if (payloadType === 'auth') {
            void gameStore.sendCustomTokenToSlotsGameUi();
            return;
          }

          if (payloadType !== 'slots') {
            return;
          }

          setTimeout(() => {
            void gameStore.handleSlotsGameIframeLoad(iframeElRef.current!);
          }, 1000);
          break;
        }
        case 'openSupport': {
          intercom.handleOpenSupport();
          break;
        }
        case 'goHome': {
          handleGoHome();
          break;
        }
        case 'headerCoinToggleClick': {
          toggleCoin();
          break;
        }
        case 'headerMenuClick': {
          handleToggle();
          break;
        }
        case 'escapeKeyClick': {
          if (uiStore.isVisibleMiniStore) {
            payRedeemStore.closeMiniStore();
          }
          break;
        }
        case 'relaunchGame': {
          if (gameStore.currentGame?.gameId) {
            void gameStore.launchGame(gameStore.currentGame.gameId, coinTypeStore.coinType, handleGoHome);
          } else {
            console.warn(`useGameIframeEvents relaunchGame message missing gameId, data: ${JSON.stringify(data)}`);
          }
          break;
        }
        case 'miniStoreClick': {
          payRedeemStore.toggleMiniStore({
            eventAction: 'click',
            triggeredBy: 'click',
            buttonLocation: 'header',
            clickId: CLICK_IDS.PURCHASE.HEADER_BALANCE_WRAPPER,
          });
          break;
        }
        case 'lowBalanceOpenMiniStore': {
          lowBalanceStore.openMiniStoreOnLowBalance(data.payload?.trigger || 'low_balance');
          break;
        }
        case 'prizeDropTimerEnd': {
          prizeDropStore.resetEvent();
          break;
        }
        case 'headerPrizeDropWidgetClick': {
          if (!prizeDropEventByCoin) {
            return;
          }

          void prizeDropStore.startPrizeDropGame({ type: 'info' });

          break;
        }
        case 'startMinigame': {
          if (!data.payload?.gameId) {
            console.warn(`useGameIframeEvents startMinigame message missing gameId, data: ${JSON.stringify(data)}`);
            return;
          }

          uiStore.setCustomLandingCoinAnimation({
            gcLandingPoint: data.payload.gcLandingPoint,
            scLandingPoint: data.payload.scLandingPoint,
          });

          void minigamesStore.startMinigame(data.payload.gameId);
          break;
        }
        default: {
          console.warn(`useGameIframeEvents Unhandled message from game UI: ${JSON.stringify(data)}`);
        }
      }
    },
    [
      gameStore,
      coinTypeStore,
      handleGoHome,
      toggleCoin,
      handleToggle,
      uiStore,
      payRedeemStore,
      prizeDropStore,
      prizeDropEventByCoin,
      minigamesStore,
      lowBalanceStore,
    ],
  );

  useEffect(() => {
    devLog('useGameIframeEvents GameIframe listening for gameUiReady message');
    window.addEventListener('message', onGameUiMessage);

    return () => {
      devLog('useGameIframeEvents GameIframe removing gameUiReady message listener');
      window.removeEventListener('message', onGameUiMessage);
    };
  }, [onGameUiMessage]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    iframeElRef.current = iframe;
    if (!loadStartTimeRef.current) {
      loadStartTimeRef.current = performance.now();
    }
    iframe.addEventListener('load', onIframeLoad);

    return () => {
      iframe.removeEventListener('load', onIframeLoad);
    };
  }, [iframeRef, onIframeLoad]);

  useEffect(() => {
    if (!gameStore.gameUiUrl || (!isIframeLoading && gameStore.slotsGameUiIframe)) {
      return;
    }

    const timeoutMs = getLoadTimeoutMs();

    const timeout = setTimeout(() => {
      if (!isIframeLoading && gameStore.slotsGameUiIframe) {
        return;
      }

      const diagnostics = { ...getTimeoutDiagnostics(), elapsedMs: getElapsedMs(), timeoutMs };
      const reason = isIframeLoading ? 'load timeout' : 'no gameUiReady message';

      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        const attempt = retryCountRef.current;

        console.warn(`Game UI iframe ${reason}, retry ${attempt}/${MAX_RETRIES} | ${JSON.stringify(diagnostics)}`);

        const iframe = iframeElRef.current;
        if (iframe) {
          const currentSrc = iframe.src;
          iframe.src = '';
          iframe.src = currentSrc;
          setIsIframeLoading(true);
        }
      } else {
        console.error(`Game UI iframe ${reason} after all retries | ${JSON.stringify(diagnostics)}`);

        gameStore.setIsGameUiError(true);
        setIsIframeLoading(false);
      }
    }, timeoutMs);

    return () => {
      clearTimeout(timeout);
    };
  }, [gameStore, isIframeLoading, gameStore.gameUiUrl, gameStore.slotsGameUiIframe]);

  return {
    isIframeLoading,
    onGameIframeError,
    retryIframeLoad,
  };
};
